/** Stav složky z `planStatus` / `metadata.projectTracking` — sdílená logika pro dashboard etapizace a seznam polygonů. */

import type { Polygon } from '../../core/models/polygon.model';
import {
  isPolygonPlanStatusInProgress,
  isPolygonPlanStatusTerminal,
  normalizePolygonPlanStatus,
  POLYGON_PLAN_STATUS_TO_FILTER_SLUG,
  type PolygonPlanStatus,
  type PolygonPlanStatusFilterSlug,
} from '@core/shared-bridge';
import { formatCsDate } from './gantt-timeline.util';

export function getPolygonProjectTracking(polygon: Polygon): Record<string, unknown> {
  const value = polygon.metadata?.['projectTracking'];
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

/**
 * Label stavu složky — stejný coalesce jako polygon-service list filtr (`planStatus` OR metadata).
 * Bez toho by „Ukázat pouze aktivní“ na Materiálech / Objektech / Finance vyhodilo prázdný dropdown,
 * když DB má jen sloupec `plan_status` a metadata.projectTracking chybí.
 */
export function polygonProjectStateLabel(polygon: Polygon): string {
  const fromColumn = typeof polygon.planStatus === 'string' ? polygon.planStatus.trim() : '';
  if (fromColumn) return fromColumn;
  const raw = getPolygonProjectTracking(polygon)['projectState'];
  return typeof raw === 'string' ? raw.trim() : '';
}

/** Canonical plan status, or null when empty / unknown. */
export function resolvePolygonPlanStatus(polygon: Polygon): PolygonPlanStatus | null {
  return normalizePolygonPlanStatus(polygonProjectStateLabel(polygon));
}

/**
 * ASCII slug for badges / Mezistav filter.
 * Empty/`neurceno` = NULL plan status; explicit `nezařazené` uses `nezarazene`.
 */
export type PolygonPlanStateKey = '' | PolygonPlanStatusFilterSlug;

/** Hodnoty filtru Mezistav na seznamu polygonů (`''` → `neurceno`). */
export type PolygonPlanStateFilterKey = PolygonPlanStatusFilterSlug | 'neurceno';

const PLAN_STATE_FILTER_SLUGS = new Set<string>([
  ...Object.values(POLYGON_PLAN_STATUS_TO_FILTER_SLUG),
  'neurceno',
]);

export function isPolygonPlanStateFilterKey(value: string): value is PolygonPlanStateFilterKey {
  return PLAN_STATE_FILTER_SLUGS.has(value);
}

/** Normalizovaný stav složky z `planStatus` / `metadata.projectTracking.projectState`. */
export function normalizePolygonPlanStateKey(polygon: Polygon): PolygonPlanStateKey {
  const normalized = resolvePolygonPlanStatus(polygon);
  if (!normalized) return '';
  return POLYGON_PLAN_STATUS_TO_FILTER_SLUG[normalized];
}

/** Klíč pro filtr Mezistav — neurčený = prázdný stav. */
export function polygonPlanStateFilterKey(
  polygon: Polygon,
): PolygonPlanStateFilterKey {
  const key = normalizePolygonPlanStateKey(polygon);
  if (!key) return 'neurceno';
  return key;
}

/** In-progress portfolio (projekce / příprava staveb / stavba); legacy maps via normalize. */
export function isPolygonActive(polygon: Polygon): boolean {
  return isPolygonPlanStatusInProgress(polygonProjectStateLabel(polygon));
}

/** Plan status `stavba` (incl. legacy `aktivní`) — manager dashboard portfolio scope. */
export function isPolygonStavba(polygon: Polygon): boolean {
  return resolvePolygonPlanStatus(polygon) === 'stavba';
}

export function filterActivePolygons(polygons: Polygon[]): Polygon[] {
  return polygons.filter(isPolygonActive);
}

export function filterStavbaPolygons(polygons: Polygon[]): Polygon[] {
  return polygons.filter(isPolygonStavba);
}

export function isPolygonProjectClosed(polygon: Polygon): boolean {
  const status = resolvePolygonPlanStatus(polygon);
  return status === 'archiv';
}

export function isPolygonProjectTerminal(polygon: Polygon): boolean {
  return isPolygonPlanStatusTerminal(polygonProjectStateLabel(polygon));
}

/** Aktuální datum (otevřená stavba) nebo datum uzavření (archiv). */
export function polygonStatusDateDisplay(polygon: Polygon): { label: string; value: string } {
  if (isPolygonProjectClosed(polygon)) {
    const end = polygon.actualConstructionEnd?.trim();
    if (end) {
      return { label: 'Datum uzavření', value: end };
    }
    const updatedAt = polygon.updatedAt;
    const updated = updatedAt instanceof Date
      ? formatCsDate(updatedAt)
      : '';
    return { label: 'Datum uzavření', value: updated || '—' };
  }
  return { label: 'Aktuální datum', value: formatCsDate(new Date()) };
}
